/**
 * The bundled reward catalogue.
 *
 * Rows and icons ship with the app instead of being read from Supabase at
 * runtime — see docs/adr/0003-reward-catalog-ships-with-the-app.md. Everything
 * here is synchronous and works offline; no network, no auth, no CDN.
 *
 * Regenerate both the JSON and the icons with `npm run tools:rewards`.
 */
import catalogJson from './rewardCatalog.json'
import { REWARD_ICON_URLS, REWARD_ICON_URLS_LARGE } from './rewardIcons.generated'

export interface CatalogReward {
  id: string
  categoryId: string | null
  /** Catalogue label ("Benitoite-Kristall"). Searchable, but never shown. */
  nameDe: string
  nameEn: string
  /** What the user reads — the reward as an action ("Einen Kristall sammeln"). */
  ctaDe: string
  ctaEn: string
  /** Squircle background colour, already resolved from the preset table. */
  hsl: [number, number, number]
  /**
   * The dark base tone (`card_base_presets`) — veil and claim button of the
   * Geschafft-Moment. `null` for the few rewards without one.
   */
  baseHsl: [number, number, number] | null
  /** The mid highlight tone (`highlight_presets`) — tints the Geschafft-Moment's shadow. */
  highlightHsl: [number, number, number] | null
  /**
   * Transparent rows under the artwork in the 256px file — measured at export.
   * The Geschafft-Moment uses it to let every image reach equally far into the
   * claim button. `null` = not measured; the moment then uses the Figma value.
   */
  artBottom: number | null
}

export interface CatalogCategory {
  id: string
  /** Stable key derived from the English name — used to pick the category icon. */
  slug: string
  nameDe: string
  nameEn: string
  sortOrder: number
}

interface CatalogFile {
  exportedAt: string
  categories: CatalogCategory[]
  rewards: CatalogReward[]
}

const catalog = catalogJson as CatalogFile

export const REWARDS: readonly CatalogReward[] = catalog.rewards
export const REWARD_CATEGORIES: readonly CatalogCategory[] = [...catalog.categories].sort(
  (a, b) => a.sortOrder - b.sortOrder,
)

const rewardById = new Map(REWARDS.map(reward => [reward.id, reward]))

// Icon URLs come from a generated module of static imports (see
// rewardIcons.generated.ts). A runtime glob would be Vite-only — the Electron
// renderer is bundled by webpack, which rejects `import.meta.glob`.
const iconUrlById = new Map<string, string>(Object.entries(REWARD_ICON_URLS))
const largeIconUrlById = new Map<string, string>(Object.entries(REWARD_ICON_URLS_LARGE))

export type CatalogLanguage = 'de' | 'en'

export function getRewardById(id: string | null | undefined): CatalogReward | null {
  if (!id) return null
  return rewardById.get(id) ?? null
}

/** Die beiden mitgelieferten Größen. 32 px im Wähler, 194 px im Geschafft-Moment. */
export type RewardIconSize = 64 | 256

/**
 * Bundled icon URL, or `undefined` if the export skipped this reward.
 *
 * `256` is the artwork for the **focus pill** and the **Geschafft-Moment**,
 * which shows it free-standing at 194px. A reward that has no large file falls
 * back to the small one — better soft than blank.
 */
export function getRewardIconUrl(id: string, size: RewardIconSize = 64): string | undefined {
  if (size === 256) return largeIconUrlById.get(id) ?? iconUrlById.get(id)
  return iconUrlById.get(id)
}

export interface RewardPalette {
  /** Dark base tone as a CSS colour, `null` = none (the surface keeps its own default). */
  base: string | null
  /** Mid highlight tone as a CSS colour, `null` = none. */
  highlight: string | null
  /** Light squircle background ("Emoji Background") as a CSS colour, `null` = no reward. */
  glow: string | null
}

const cssHsl = ([h, s, l]: [number, number, number]) => `hsl(${h} ${s}% ${l}%)`

/**
 * The colours a reward brings to the **Geschafft-Moment**: the base for veil,
 * claim button and frame, the highlight for the tint of the card's shadow, the
 * squircle background for the shape glowing under the claim button.
 * Empty fields are deliberate: the moment's own tokens stand in, so the
 * defaults live in ONE place — `globals.scss`.
 */
export function getRewardPalette(reward: CatalogReward | null): RewardPalette {
  return {
    base: reward?.baseHsl ? cssHsl(reward.baseHsl) : null,
    highlight: reward?.highlightHsl ? cssHsl(reward.highlightHsl) : null,
    glow: reward ? cssHsl(reward.hsl) : null,
  }
}

/** German is the fallback: the catalogue is curated in German first. */
export function rewardName(reward: CatalogReward, language: CatalogLanguage): string {
  if (language === 'en') return reward.nameEn || reward.nameDe
  return reward.nameDe || reward.nameEn
}

/**
 * The reward phrased as an action — this is what the picker and the badge show.
 * Falls back to the catalogue name if an entry has no CTA yet.
 */
export function rewardCta(reward: CatalogReward, language: CatalogLanguage): string {
  const cta = language === 'en' ? reward.ctaEn || reward.ctaDe : reward.ctaDe || reward.ctaEn
  return cta || rewardName(reward, language)
}

export function categoryName(category: CatalogCategory, language: CatalogLanguage): string {
  if (language === 'en') return category.nameEn || category.nameDe
  return category.nameDe || category.nameEn
}

export interface RewardFilter {
  query?: string
  /** Empty or omitted = all categories (matches the picker's filter chip). */
  categoryIds?: readonly string[]
}

/**
 * Search matches BOTH languages regardless of the active locale — a German user
 * typing "coffee" should still find "Einen Kaffee in Ruhe genießen".
 *
 * Names are searched alongside the visible CTAs: "Benitoite-Kristall" is not on
 * screen anywhere, but typing it should still surface "Einen Kristall sammeln".
 */
export function filterRewards({ query, categoryIds }: RewardFilter = {}): CatalogReward[] {
  const needle = query?.trim().toLowerCase() ?? ''
  const categories = categoryIds && categoryIds.length > 0 ? new Set(categoryIds) : null

  return REWARDS.filter(reward => {
    if (categories && (!reward.categoryId || !categories.has(reward.categoryId))) return false
    if (!needle) return true
    return (
      reward.ctaDe.toLowerCase().includes(needle) ||
      reward.ctaEn.toLowerCase().includes(needle) ||
      reward.nameDe.toLowerCase().includes(needle) ||
      reward.nameEn.toLowerCase().includes(needle)
    )
  })
}

export interface RewardGroup {
  category: CatalogCategory
  rewards: CatalogReward[]
}

/** Groups in curated category order; categories without a hit are dropped. */
export function groupRewardsByCategory(rewards: readonly CatalogReward[]): RewardGroup[] {
  const byCategory = new Map<string, CatalogReward[]>()
  for (const reward of rewards) {
    if (!reward.categoryId) continue
    const bucket = byCategory.get(reward.categoryId)
    if (bucket) bucket.push(reward)
    else byCategory.set(reward.categoryId, [reward])
  }

  return REWARD_CATEGORIES.flatMap(category => {
    const group = byCategory.get(category.id)
    return group && group.length > 0 ? [{ category, rewards: group }] : []
  })
}
